import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import AvailableSessionsChip from 'components/shared/chips/AvailableSessionsChip';
import MembershipStatusChip from 'components/shared/chips/MembershipStatusChip';
import { dateFormat } from 'common/constants/date';
import { roles } from 'common/constants/options';
import StyledDrawerMenuLabel from 'common/theme/drawer-menu/StyledDrawerMenuLabel';
import StyledDrawerMenuMembershipDate from 'common/theme/drawer-menu/StyledDrawerMenuMembershipDate';
import StyledDrawerMenuUserInfoContainer from 'common/theme/drawer-menu/StyledDrawerMenuUserInfoContainer';
import StyledDrawerMenuUsername from 'common/theme/drawer-menu/StyledDrawerMenuUsername';
import StyledDrawerMenuUserStatus from 'common/theme/drawer-menu/StyledDrawerMenuUserStatus';

function DrawerUserInfo() {
  const { t, i18n } = useTranslation(['labels', 'options']);

  const availableSessions = useSelector((state) => state.auth.user.available_sessions);
  const personalizedSessions = useSelector((state) => state.auth.user.personalized_sessions ?? 0);
  const fullName = useSelector((state) => state.auth.user.full_name);
  const gender = useSelector((state) => state.auth.user.gender);
  const joined = useSelector((state) => state.auth.user.created_at);
  const membershipStatus = useSelector((state) => state.auth.user.membership_status);
  const role = useSelector((state) => state.auth.user.role);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  // El contacto de RRHH es un CLIENT con flag, pero no entrena: su membresía y sus sesiones no
  // significan nada en un panel que solo sirve para consultar reportes.
  const isOrgManager = useSelector((state) => Boolean(state.auth.user.is_org_manager));
  const organizationName = useSelector((state) => state.auth.user.organization?.name);

  const dateLocale = i18n.language === 'es' ? es : enUS;

  // Qué se muestra bajo el nombre: el gestor de empresa ve su rol y su empresa; el staff, su rol;
  // y el cliente que entrena, desde cuándo lo hace.
  const renderIdentity = () => {
    if (isOrgManager) {
      return (
        <>
          <StyledDrawerMenuLabel>
            {t('orgManagerRole', { ns: 'labels', defaultValue: 'Contacto de bienestar' })}
          </StyledDrawerMenuLabel>
          {organizationName && <StyledDrawerMenuLabel>{organizationName}</StyledDrawerMenuLabel>}
        </>
      );
    }

    if (role !== roles.client) {
      return (
        <>
          <StyledDrawerMenuLabel>
            {t(`role.${role.toLowerCase()}`, { ns: 'options' })}
          </StyledDrawerMenuLabel>
          <StyledDrawerMenuLabel>Otrix Team</StyledDrawerMenuLabel>
        </>
      );
    }

    return (
      <StyledDrawerMenuMembershipDate>
        {t('titanSince', { ns: 'labels' })}:{' '}
        {format(parseISO(joined), dateFormat, { locale: dateLocale })}
      </StyledDrawerMenuMembershipDate>
    );
  };
  // El chip de sesiones 1-a-1 se muestra si el cliente TIENE sesiones individuales. Un Active Plus
  // PURO (sin plan regular) no agenda 1-a-1 → su contador en 0 confunde y se oculta. Pero un cliente
  // DUAL (plan regular + Active Plus) SÍ tiene 1-a-1, así que debe verlo.
  const oneOnOneSessions = personalizedSessions > 0 ? personalizedSessions : availableSessions;

  return (
    <>
      <StyledDrawerMenuUserInfoContainer>
        <StyledDrawerMenuUsername>{fullName}</StyledDrawerMenuUsername>
        {renderIdentity()}
      </StyledDrawerMenuUserInfoContainer>
      {role === roles.client && !isOrgManager && (
        <StyledDrawerMenuUserStatus>
          <MembershipStatusChip context={gender.toLowerCase()} status={membershipStatus} />
          {(!isActivePlus || oneOnOneSessions > 0) && (
            <AvailableSessionsChip availableSessions={availableSessions} personalizedSessions={personalizedSessions} />
          )}
        </StyledDrawerMenuUserStatus>
      )}
    </>
  );
}

export default DrawerUserInfo;
